import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  ArrowRight, Cpu, Zap, Clock, Globe, Brain, Shield,
  TrendingUp, Code, Settings, GitBranch, Layers
} from 'lucide-react';

const AIAgentDevelopment = () => {
  const agentTypes = [
    {
      icon: Brain,
      title: 'Research & Analysis Agents',
      description: 'Agents that browse the web, pull from databases, synthesise reports, and surface insights autonomously',
      useCases: ['Competitive intelligence', 'Market research', 'Due diligence reports', 'News monitoring & summaries']
    },
    {
      icon: TrendingUp,
      title: 'Sales & Outreach Agents',
      description: 'Fully autonomous prospecting — find leads, enrich data, personalise outreach, and log to CRM without human input',
      useCases: ['Lead discovery & enrichment', 'Personalised email sequences', 'LinkedIn outreach', 'Follow-up automation']
    },
    {
      icon: Settings,
      title: 'Data Processing Agents',
      description: 'End-to-end data pipelines where the agent reads, transforms, validates, and writes data across systems',
      useCases: ['Invoice & document extraction', 'ERP data sync', 'Data quality checks', 'Automated reporting']
    },
    {
      icon: Layers,
      title: 'Multi-Agent Orchestration',
      description: 'Architect systems where multiple specialised agents collaborate — planner, executor, verifier — to handle complex workflows',
      useCases: ['LangGraph agent networks', 'Tool-calling pipelines', 'Human-in-the-loop approval', 'Parallel task execution']
    },
    {
      icon: Code,
      title: 'Engineering & DevOps Agents',
      description: 'Agents that write code, run tests, fix bugs, and open PRs — accelerating your engineering velocity',
      useCases: ['Code generation & review', 'Test generation', 'Infrastructure provisioning', 'Incident triage']
    },
    {
      icon: Globe,
      title: 'Customer-Facing Agents',
      description: 'Conversational agents with memory, tool use, and the ability to take real actions inside your systems',
      useCases: ['Order management', 'Account self-service', 'Booking & scheduling', 'Returns & complaints']
    }
  ];

  const caseStudies = [
    {
      industry: 'Manufacturing',
      challenge: 'Manual demand forecasting taking 3 days per cycle — inaccurate and too slow for procurement',
      solution: 'Multi-step AI agent pulling sales data, running forecasting models, and generating purchase orders autonomously',
      result: '50% better forecast accuracy. Cycle time: 3 days → 4 hours.',
      timeline: '5 weeks',
      link: '/case-studies/manufacturing-ai'
    },
    {
      industry: 'Insurance',
      challenge: 'Claims processing required 5 manual touches across 3 systems, taking 2-3 days per claim',
      solution: 'Document extraction + classification agent integrated with claims management system',
      result: '95% straight-through processing. Average handling time cut from 2 days to 11 minutes.',
      timeline: '6 weeks',
      link: '/case-studies/insurance-automation'
    },
    {
      industry: 'Retail',
      challenge: 'Price benchmarking required an analyst to manually visit 50+ competitor sites daily',
      solution: 'Web-browsing agent that monitors competitors, detects price changes, and alerts the pricing team',
      result: '100% competitor coverage. 8 hours of analyst time saved daily.',
      timeline: '3 weeks',
      link: '/case-studies/retail-analytics'
    }
  ];

  const process = [
    { step: '01', title: 'Agent Design Workshop', description: 'Map the exact workflow, define tool access, guardrails, and success metrics for your agent', duration: '3–5 days' },
    { step: '02', title: 'Tool & Integration Build', description: 'Build every tool the agent needs — APIs, DB connections, web scraping, file I/O, external services', duration: '1–2 weeks' },
    { step: '03', title: 'Agent Development & Evals', description: 'Build the agent loop, test against real scenarios, tune prompts and routing logic, measure reliability', duration: '2–4 weeks' },
    { step: '04', title: 'Deploy & Monitor', description: 'Production deployment with observability, failure alerting, and human escalation paths', duration: '1 week' }
  ];

  const techStack = [
    { category: 'Agent Frameworks', items: ['LangGraph', 'LangChain Agents', 'AutoGen', 'CrewAI', 'Custom loops'] },
    { category: 'LLMs', items: ['GPT-4o', 'Claude Opus / Sonnet', 'Gemini Pro', 'Llama 3.1', 'Mistral Large'] },
    { category: 'Tool Calling', items: ['OpenAI Function Calling', 'Claude Tool Use', 'MCP (Model Context Protocol)'] },
    { category: 'Memory & RAG', items: ['Pinecone', 'Weaviate', 'pgvector', 'Redis', 'Chroma'] },
    { category: 'Orchestration', items: ['Kubernetes', 'Celery', 'Temporal', 'AWS Step Functions', 'Docker'] },
    { category: 'Observability', items: ['LangSmith', 'Weights & Biases', 'Helicone', 'Custom dashboards'] }
  ];

  return (
    <>
      <Helmet>
        <title>AI Agent Development Company | Autonomous AI Agents Built in Weeks | Agility</title>
        <meta name="description" content="Build autonomous AI agents that execute multi-step workflows without human input. Research agents, sales agents, data processing agents, multi-agent systems. Deployed in 3-6 weeks by senior AI engineers." />
        <meta name="keywords" content="AI agent development, autonomous AI agents, AI workflow automation, build AI agent, AI agent development company, multi-agent AI systems, LangGraph development, AI automation agents, agentic AI development, custom AI agents" />
        <link rel="canonical" href="https://www.agilitytech.ai/ai-agent-development" />
        <meta property="og:title" content="AI Agent Development | Autonomous Agents Built in Weeks | Agility" />
        <meta property="og:description" content="Autonomous AI agents that execute complex workflows without human input. Research, sales, data, DevOps agents. Deployed in 3-6 weeks." />
        <meta property="og:url" content="https://www.agilitytech.ai/ai-agent-development" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://www.agilitytech.ai/agility hex fonts.png" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Service",
            "name": "AI Agent Development",
            "description": "Custom autonomous AI agent development — from single-purpose agents to multi-agent orchestration systems deployed in production.",
            "provider": { "@type": "Organization", "name": "Agility", "url": "https://www.agilitytech.ai" },
            "areaServed": "Worldwide",
            "serviceType": "AI Agent Development"
          })}
        </script>
      </Helmet>

      {/* Hero */}
      <section className="relative min-h-[90vh] flex items-center justify-center overflow-hidden bg-gradient-to-br from-indigo-900 via-black to-teal-900">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute inset-0 bg-[linear-gradient(30deg,#14b8a6_12%,transparent_12.5%,transparent_87%,#14b8a6_87.5%,#14b8a6),linear-gradient(150deg,#14b8a6_12%,transparent_12.5%,transparent_87%,#14b8a6_87.5%,#14b8a6)]" style={{ backgroundSize: '80px 140px' }} />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-teal-500/10 border border-teal-500/30 rounded-full mb-8">
            <Cpu className="w-4 h-4 text-teal-400" />
            <span className="text-teal-300 text-sm font-medium">Autonomous AI Agents</span>
          </div>
          <h1 className="text-5xl md:text-7xl font-bold text-white mb-6 leading-tight">
            AI Agents That <span className="text-teal-400">Do the Work</span>
          </h1>
          <p className="text-xl text-gray-300 leading-relaxed mb-8 max-w-3xl mx-auto">
            Autonomous agents that plan, use tools, and execute multi-step workflows — no human in the loop.
            Built with LangGraph, Claude, and GPT-4o. Deployed in 3–6 weeks.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
            <Link to="/contact" className="group inline-flex items-center justify-center gap-2 bg-teal-500 hover:bg-teal-600 text-white px-8 py-4 rounded-lg font-semibold transition-all">
              Design My Agent <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Link>
            <Link to="/case-studies" className="group inline-flex items-center justify-center gap-2 bg-white/10 hover:bg-white/20 text-white px-8 py-4 rounded-lg font-semibold backdrop-blur-sm border border-white/20 transition-all">
              See Agent Results <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 max-w-4xl mx-auto">
            {[['3–6', 'Weeks to Prod'],['90%+', 'Task Automation Rate'],['200+', 'AI Projects Shipped'],['0', 'Human Steps Required']].map(([stat, label]) => (
              <div key={label} className="text-center">
                <div className="text-4xl font-bold text-teal-400 mb-2">{stat}</div>
                <div className="text-gray-400">{label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Agent Types */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Agents for <span className="text-teal-600">Every Function</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              We build agents that slot into your business workflows and execute tasks that used to require human staff
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {agentTypes.map((agent, i) => {
              const Icon = agent.icon;
              return (
                <div key={i} className="fade-in-up bg-gradient-to-br from-gray-50 to-white rounded-xl p-8 border-2 border-gray-200 hover:border-teal-500 transition-all">
                  <Icon className="w-12 h-12 text-teal-600 mb-6" />
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">{agent.title}</h3>
                  <p className="text-gray-600 mb-6">{agent.description}</p>
                  <ul className="space-y-2">
                    {agent.useCases.map((uc, j) => (
                      <li key={j} className="flex items-center gap-2 text-sm text-gray-700">
                        <Zap className="w-4 h-4 text-teal-500 flex-shrink-0" /> {uc}
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Case Studies */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Agents <span className="text-teal-600">in Production</span>
            </h2>
          </div>
          <div className="grid lg:grid-cols-3 gap-8">
            {caseStudies.map((study, i) => (
              <Link key={i} to={study.link} className="fade-in-up bg-gradient-to-br from-teal-50 to-white rounded-xl p-8 border border-teal-200 hover:border-teal-400 hover:shadow-lg transition-all group">
                <div className="text-sm font-semibold text-teal-600 mb-4">{study.industry}</div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Challenge</h3>
                <p className="text-gray-700 mb-4">{study.challenge}</p>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Solution</h3>
                <p className="text-gray-700 mb-4">{study.solution}</p>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Results</h3>
                <p className="text-gray-700 mb-6">{study.result}</p>
                <div className="flex items-center justify-between pt-6 border-t border-teal-200">
                  <div>
                    <div className="text-sm text-gray-500">Timeline</div>
                    <div className="font-semibold text-gray-900">{study.timeline} to production</div>
                  </div>
                  <div className="flex items-center gap-1 text-teal-600 font-semibold text-sm group-hover:translate-x-1 transition-transform">
                    Read more <ArrowRight className="w-4 h-4" />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              How We <span className="text-teal-600">Build Your Agent</span>
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {process.map((p, i) => (
              <div key={i} className="fade-in-up text-center">
                <div className="w-20 h-20 bg-teal-500 text-white rounded-full flex items-center justify-center text-3xl font-bold mx-auto mb-6">{p.step}</div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{p.title}</h3>
                <p className="text-gray-600 mb-4">{p.description}</p>
                <div className="inline-flex items-center gap-2 px-3 py-1 bg-teal-50 text-teal-700 rounded-full text-sm font-medium">
                  <Clock className="w-4 h-4" /> {p.duration}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Tech Stack */}
      <section className="py-24 bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Agent <span className="text-teal-400">Tech Stack</span>
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {techStack.map((t, i) => (
              <div key={i} className="bg-gray-800 rounded-xl p-6 border border-gray-700">
                <h3 className="text-lg font-bold text-teal-400 mb-4">{t.category}</h3>
                <div className="flex flex-wrap gap-2">
                  {t.items.map((item, j) => (
                    <span key={j} className="px-3 py-1 bg-gray-700 text-gray-200 rounded-full text-sm">{item}</span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-gradient-to-br from-teal-600 to-teal-800">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">Build an Agent That Works While You Sleep</h2>
          <p className="text-xl text-teal-100 mb-8 max-w-2xl mx-auto">
            Tell us the workflow you want to automate. We'll scope it and have a working agent prototype in under two weeks.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact" className="group inline-flex items-center justify-center gap-2 bg-white text-teal-700 hover:bg-teal-50 px-8 py-4 rounded-lg font-bold text-lg transition-all">
              Get a Free Agent Scoping Call <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
        </div>
      </section>
    </>
  );
};

export default AIAgentDevelopment;
